import React, { useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import { TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DataProviderInstance } from '../../api/DataProvider';
import { ageAt, ageLabel, hasValidBirthday, milestoneRows } from '../../pages/appointment/modules/motorMilestones/motorMilestones';
import styles from './MotorMilestonePopup.module.css';

const DATE_FORMAT = 'YYYY-MM-DD';
const NOTE_MAX = 255;

/** Data padrão: a do atendimento (nunca no futuro), senão hoje. */
const defaultDate = (appointmentDate) => {
  const today = dayjs().format(DATE_FORMAT);
  if (!appointmentDate || !dayjs(appointmentDate).isValid()) return today;
  const date = dayjs(appointmentDate).format(DATE_FORMAT);
  return date > today ? today : date;
};

/**
 * Registro ou edição da conquista de um marco motor: data e observação.
 * Mostra o critério do marco, a idade (corrigida, se prematuro) na data e a
 * situação frente à janela da OMS enquanto se escolhe a data.
 */
function MotorMilestonePopup({ patientId, appointmentId, appointmentDate, milestone, record, birthday, birthInfo, OnHide }) {
  const { SetPatientMotorMilestone, DeletePatientMotorMilestone } = DataProviderInstance();

  const [date, setDate] = useState(record ? dayjs(record.achievedAt).format(DATE_FORMAT) : defaultDate(appointmentDate));
  const [note, setNote] = useState(record?.note || '');
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });

  const age = date ? ageAt(birthday, date, birthInfo) : null;
  const preview = date
    ? milestoneRows([{ milestoneKey: milestone.key, achievedAt: date }], birthday, birthInfo, date).find((row) => row.milestone.key === milestone.key)
    : null;

  function saveHandler() {
    if (saving) return;
    if (!date) {
      showToast('Informe a data em que o marco foi conquistado.');
      return;
    }

    setSaving(true);
    const payload = { milestoneKey: milestone.key, achievedAt: date, note: note.trim() };
    SetPatientMotorMilestone(patientId, record ? record.appointmentId : appointmentId, payload, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast(response?.error || 'Não foi possível salvar o marco.');
      }
    });
  }

  function deleteHandler() {
    DeletePatientMotorMilestone(record.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir o marco.');
      }
    });
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.criteria}>{milestone.hint}</div>

      <div className={styles.header}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Conquistado em"
            value={date ? dayjs(date) : null}
            format="DD/MM/YYYY"
            disableFuture={true}
            minDate={hasValidBirthday(birthday) ? dayjs(birthday) : undefined}
            onChange={(value) => setDate(value && value.isValid() ? value.format(DATE_FORMAT) : null)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        {age && (
          <span className={styles.age}>Idade na conquista: <strong>{ageLabel(age)}</strong></span>
        )}
      </div>

      {preview && age && milestone.window && (
        <span className={styles.hint}>{preview.status.label}.</span>
      )}

      <TextField
        size="small"
        label="Observação (opcional)"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
        multiline
        minRows={2}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {record && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
        />
      )}

      <CustomButton
        variant="outlined"
        style="secondary"
        label="Cancelar"
        onClick={() => OnHide(false)}
        enabled={!saving}
      />

      <CustomButton
        style="primary"
        variant="contained"
        label={record ? 'Atualizar' : 'Registrar'}
        icon={<IconSave />}
        onClick={saveHandler}
        loading={saving}
        enabled={!saving}
      />

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir o registro deste marco? Ele volta a ficar pendente."
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={milestone.label}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
      maxWidth="sm"
    />
  );
}

export default MotorMilestonePopup;
